import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useInstallAppBanner } from "@/contexts";
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  APP_STORE_BADGE_SRC,
  GOOGLE_PLAY_BADGE_SRC,
} from "@/constants/appStore";
import { isAndroidUA } from "@/utils/device";

/**
 * Non-blocking bottom-left toast that nudges users to install the mobile app.
 * Crucially: NO backdrop, NO layout shift on the page behind it. The user
 * keeps using the Genie page underneath. Dismissible via X.
 *
 * Visual language follows the install-mascot pattern used in the email
 * templates (mascot-phone + store badges) — not a QR-first design.
 */
export const InstallAppBanner = () => {
  const { isOpen, dismissBanner } = useInstallAppBanner();
  const isAndroid = useMemo(isAndroidUA, []);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="install-app-toast"
          initial={{ opacity: 0, x: -32, y: 8 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: -32, y: 8 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-[1200] w-[340px] max-w-[calc(100vw-32px)] rounded-2xl bg-white shadow-[0_18px_45px_rgba(15,23,42,0.18)] ring-1 ring-black/[0.04] overflow-hidden"
          style={{
            left: "max(16px, env(safe-area-inset-left))",
            bottom: "max(16px, env(safe-area-inset-bottom))",
          }}
          role="region"
          aria-label="Install Help Genie app"
        >
          {/* Soft brand-gradient halo strip across the top */}
          <div className="h-1 w-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />

          <button
            type="button"
            onClick={dismissBanner}
            className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full text-[#9CA3AF] hover:text-[#222B45] hover:bg-black/[0.04] transition-colors"
            aria-label="Dismiss install banner"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-3 p-4 pr-9">
            <img
              src="/email-assets/mascot-phone.png"
              alt=""
              className="w-[72px] h-[72px] flex-shrink-0 object-contain select-none pointer-events-none"
              draggable={false}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#222B45] leading-tight">Take your Genies with you</p>
              <p className="mt-0.5 text-xs text-[#64748B] leading-snug">
                Install the Help Genie app and keep talking on the go.
              </p>

              <div className={`mt-2.5 flex items-center ${isAndroid ? "flex-row-reverse" : "flex-row"} gap-2`}>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center transition-transform hover:scale-105"
                  aria-label="Download on the App Store"
                >
                  <img
                    src={APP_STORE_BADGE_SRC}
                    alt="Download on the App Store"
                    className="h-9 w-auto select-none"
                    draggable={false}
                  />
                </a>
                <a
                  href={GOOGLE_PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center transition-transform hover:scale-105"
                  aria-label="Get it on Google Play"
                >
                  <img
                    src={GOOGLE_PLAY_BADGE_SRC}
                    alt="Get it on Google Play"
                    className="h-9 w-auto select-none"
                    draggable={false}
                  />
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallAppBanner;
